import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  styled,
  Button,
  useMediaQuery,
  useTheme,
  Link,
} from "@mui/material";

/**
 * 1) A custom gradient button matching your design
 */
const GradientButton = styled(Button)(({ theme }) => ({
  fontFamily: '"Lexend", sans-serif',
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: 8,
  padding: "14px",
  width: "100%",
  fontSize: "1rem",
  "&:hover": {
    background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
  },
}));

/**
 * 2) A styled TextField for the code boxes:
 *    - Square shape
 *    - Centered text
 *    - Big font
 */
const CodeTextField = styled(TextField)(() => ({
  width: 60,
  height: 60,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    fontSize: "1.5rem",
    textAlign: "center",
    fontWeight: 600,
    "& input": {
      textAlign: "center",
      padding: 0,
    },
  },
}));

/**
 * Main VerifyEmail component
 */
export default function VerifyEmailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 5 code digits
  const [codes, setCodes] = useState(["", "", "", "", ""]);

  // Handle code input changes
  const handleChange = (index: number, value: string) => {
    // Only accept single digit
    if (value.length > 1) return;
    const newCodes = [...codes];
    newCodes[index] = value;
    setCodes(newCodes);
  };

  // Focus next box automatically
  const handleKeyUp = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !codes[index] && index > 0) {
      (e.currentTarget.parentElement?.previousElementSibling?.querySelector(
        "input"
      ) as HTMLInputElement)?.focus();
    } else if (/[0-9]/.test(e.key) && index < 4) {
      (e.currentTarget.parentElement?.nextElementSibling?.querySelector(
        "input"
      ) as HTMLInputElement)?.focus();
    }
  };

  // “Verify” button click
  const handleVerify = () => {
    // e.g. do something with the 5-digit code
    alert("Code entered: " + codes.join(""));
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#f8f8f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: isMobile ? "90%" : 480,
          maxWidth: 600,
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
        }}
      >
        {/* Logo and Heading */}
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            letterSpacing: 1,
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 700,
          }}
        >
          StageLink
        </Typography>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontFamily: '"Lexend", sans-serif', fontWeight: 700 }}
        >
          Verify your email address
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 3, fontFamily: '"Lexend", sans-serif', color: "#666" }}
        >
          We’ve sent a code to helloworld@gmail.com{" "}
          <Link
            href="#"
            underline="hover"
            sx={{ fontFamily: '"Lexend", sans-serif' }}
          >
            Change
          </Link>
        </Typography>

        {/* Code inputs row */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            mb: 3,
          }}
        >
          {codes.map((code, i) => (
            <CodeTextField
              key={i}
              variant="outlined"
              value={code}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyUp={(e) => handleKeyUp(e, i)}
              inputProps={{ maxLength: 1 }}
            />
          ))}
        </Box>

        {/* Verify Button */}
        <GradientButton onClick={handleVerify}>Verify</GradientButton>

        {/* Resend link */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            fontFamily: '"Lexend", sans-serif',
            color: "#666",
          }}
        >
          Send code again <span style={{ fontWeight: 600 }}>00:04</span>
        </Typography>
      </Paper>
    </Box>
  );
}
